/* eslint-disable arrow-parens, max-len */
import React, { useState, useEffect } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'
import { useToasts } from 'react-toast-notifications'
import { getUserInfo } from '_utils/localData'
import userApi from '_api/user'
import orderApi from '_api/order'
import moment from 'moment'
import DatePicker from '../../../Event/components/DatePicker'
import PurchaseDetailAddAddressModal from './PurchaseDetailAddAddressModal'

const AmendDeliveryAddressModal = ({
  isOpen,
  toggle,
  orderRef,
  currentBookingAddress,
  onAddressUpdated,
  deliveryDateParent
}) => {
  const { addToast } = useToasts()
  const userInfo = getUserInfo()
  const [userAddresses, setUserAddresses] = useState([])
  const [selectedAddressId, setSelectedAddressId] = useState('')
  const [editingAddress, setEditingAddress] = useState(null)
    const [editForm, setEditForm] = useState({
    name: '',
    address: '',
    postalCode: '',
    phone: '',
    consignee: '',
    isDefault: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isAddNewAddressModalOpen, setIsAddNewAddressModalOpen] = useState(false)

  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)

  const parseDeliveryDate = (val) => {
    if (!val) return null
    if (val instanceof Date && !isNaN(val.getTime())) return val
    const m = moment(val, 'DD-MM-YYYY', true)
    if (m.isValid()) return m.toDate()
    const iso = new Date(val)
    return !isNaN(iso.getTime()) ? iso : null
  }

  const [deliveryDate, setDeliveryDate] = useState(parseDeliveryDate(deliveryDateParent) || todayStart)

  const fallbackPickupLimit = (() => {
    const addrWithPickup = userAddresses.find(a => a.eventDetail?.pickupTimestamp)
    return addrWithPickup ? new Date(addrWithPickup.eventDetail.pickupTimestamp * 1000) : null
  })()

  const effectivePickupLimit = fallbackPickupLimit || null

  const findMatchingAddress = (addresses, bookingAddress) => {
    if (!addresses.length || !bookingAddress) return null

    for (const address of addresses) {
      const addressStr = address.address?.street || address.address || ''
      const postalCode = address.postalCode || ''
      const country = address.country || 'Singapore'
      // "street, postalCode, country"
      const formattedAddress = `${addressStr}, ${postalCode}, ${country}`

      if (formattedAddress === bookingAddress) {
        return address
      }

      const variations = [
        `${addressStr}, ${postalCode}, ${country}`,
        `${addressStr}, ${country}, ${postalCode}`,
        `${addressStr}, Singapore, ${postalCode}`,
        `${addressStr}, ${postalCode}`
      ]
      for (const variation of variations) {
        if (variation === bookingAddress) {
          return address
        }
      }

      if (bookingAddress.includes(addressStr) && bookingAddress.includes(postalCode)) {
        return address
      }
    }
    return null
  }

  const fetchUserAddresses = async () => {
    try {
      const response = await userApi.listUserAddress()
      const addresses = response?.addresses || []
      setUserAddresses(addresses)

      if (currentBookingAddress) {
        const matchingAddress = findMatchingAddress(addresses, currentBookingAddress)
        if (matchingAddress) {
          setSelectedAddressId(matchingAddress.id)
          setDeliveryDate(parseDeliveryDate(matchingAddress.deliveryDate) || parseDeliveryDate(deliveryDateParent) || todayStart)
        } else {
          setDeliveryDate(parseDeliveryDate(deliveryDateParent) || todayStart)
        }
      } else {
        setDeliveryDate(parseDeliveryDate(deliveryDateParent) || todayStart)
      }
    } catch (err) {
      addToast('Failed to load addresses', { appearance: 'error', autoDismiss: true })
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchUserAddresses()
      setDeliveryDate(parseDeliveryDate(deliveryDateParent) || todayStart)
    }
  }, [isOpen, currentBookingAddress])

  // Reset selected address
  useEffect(() => {
    if (!isOpen) {
      setSelectedAddressId('')
      setEditingAddress(null)
    }
  }, [isOpen])

  const handleSelectAddress = (address) => {
    setSelectedAddressId(address.id)
    setDeliveryDate(parseDeliveryDate(address.deliveryDate) || parseDeliveryDate(deliveryDateParent) || todayStart)
  }

  const handleConfirm = async () => {
    const selected = userAddresses.find(addr => addr.id === selectedAddressId)
    if (selected) {
      setIsLoading(true)
      try {
        const addressStr = selected.address?.street || selected.address || ''
        const postalCode = selected.postalCode || ''
        const country = selected.country || 'Singapore'

        const newBookingAddress = `${addressStr}, ${country}, ${postalCode}`
        const formattedDeliveryDate = deliveryDate ? moment(deliveryDate).format('DD-MM-YYYY') : null

        await orderApi.updateOrderAddress(orderRef, {
          bookingAddress: newBookingAddress,
          ...(formattedDeliveryDate ? { deliveryDate: formattedDeliveryDate } : {}),
          phone: selected.phone || userInfo?.phone || '',
          consignee: selected.consignee || userInfo?.name || ''
        })

        addToast('Delivery address updated successfully!', { appearance: 'success', autoDismiss: true })

        if (onAddressUpdated) {
          onAddressUpdated(newBookingAddress, formattedDeliveryDate, selected.consignee || '')
        }

        toggle()
      } catch (err) {
        addToast(err?.msgResp || 'Failed to update delivery address', { appearance: 'error', autoDismiss: true })
      } finally {
        setIsLoading(false)
      }
    } else {
      addToast('Please select an address', { appearance: 'error', autoDismiss: true })
    }
  }

  const handleAddNew = () => {
    setIsAddNewAddressModalOpen(true)
  }

  const handleEditClick = (address, e) => {
    e.stopPropagation()
    setEditingAddress(address)
    setEditForm({
      name: address.name,
      address: address.address?.street || address.address,
      postalCode: address.postalCode,
      phone: address.phone,
      consignee: address.consignee || '',
      isDefault: address.isDefault || false
    })
  }

  const handleDeleteClick = async (addressId, e) => {
    e.stopPropagation()
    if (window.confirm('Are you sure you want to delete this address?')) {
      setIsLoading(true)
      try {
        await userApi.deleteUserAddress(addressId)
        addToast('Address deleted successfully!', { appearance: 'success', autoDismiss: true })
        await fetchUserAddresses()

        if (selectedAddressId === addressId) {
          setSelectedAddressId('')
        }
      } catch (err) {
        addToast(err?.msgResp || 'Failed to delete address', { appearance: 'error', autoDismiss: true })
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleEditSave = async () => {
    if (!editForm.name.trim() || !editForm.address.trim() || !editForm.postalCode.trim()) {
      addToast('Please fill in all required fields', { appearance: 'error', autoDismiss: true })
      return
    }

    setIsLoading(true)
    try {
      await userApi.updateUserAddress({
        addressId: editingAddress.id,
        name: editForm.name.trim(),
        address: editForm.address.trim(),
        consignee: editForm.consignee.trim(),
        postalCode: editForm.postalCode.trim(),
        phone: editForm.phone.trim(),
        isDefault: editForm.isDefault
      })

      addToast('Address updated successfully!', { appearance: 'success', autoDismiss: true })
      setEditingAddress(null)
      await fetchUserAddresses()
    } catch (err) {
      addToast(err?.msgResp || 'Failed to update address', { appearance: 'error', autoDismiss: true })
    } finally {
      setIsLoading(false)
    }
  }

  const handleEditCancel = () => {
    setEditingAddress(null)
    setEditForm({ name: '', address: '', postalCode: '', phone: '', consignee: '', isDefault: false })
  }

  const handleAddressAdded = async (signal) => {
    setIsAddNewAddressModalOpen(false)

    if (signal === 'CLOSE_PARENT_MODAL') {
      toggle()
      return
    }

    // Refresh addresses list
    await fetchUserAddresses()

    if (signal) {
      setSelectedAddressId(signal)
    } else {
      const response = await userApi.listUserAddress()
      const addresses = response?.addresses || []
      if (addresses.length > 0) {
        const newestAddress = addresses[addresses.length - 1]
        setSelectedAddressId(newestAddress.id)
      }
    }
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="lg"
        centered={true}
        style={{
          maxWidth: '498px'
        }}
      >
        <ModalHeader toggle={toggle}>
          {editingAddress ? 'Edit Address' : 'Amend Delivery Address'}
        </ModalHeader>
        <ModalBody>
          {editingAddress ? (
            <div>
              <div className="form-group mb-3">
                <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Address Name <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="e.g. Home, Office"
                />
              </div>
              <div className="form-group mb-3">
                <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Consignee
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.consignee}
                  onChange={(e) => setEditForm({ ...editForm, consignee: e.target.value })}
                  placeholder="Recipient name"
                />
              </div>

              <div className="form-group mb-3">
                <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Full Address <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  placeholder="Street, building, unit number"
                />
              </div>

              <div className="form-group mb-3">
                <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Postal Code <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.postalCode}
                  onChange={(e) => setEditForm({ ...editForm, postalCode: e.target.value })}
                  placeholder="123456"
                />
              </div>

              <div className="form-group mb-3">
                <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Phone Number
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>

              <div className="form-group mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="editIsDefault"
                  checked={editForm.isDefault}
                  onChange={(e) => setEditForm({ ...editForm, isDefault: e.target.checked })}
                />
                <label
                  className="form-check-label"
                  htmlFor="editIsDefault"
                  style={{ fontSize: '14px', fontWeight: '500' }}
                >
                  Set as default address
                </label>
              </div>
            </div>
          ) : (
            userAddresses.length > 0 ? (
              <div>
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: '14px', fontWeight: 600, marginBottom: 8, display: 'block' }}>
                      Delivery Date <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <div className="have-icon" style={{ width: '50%' }}>
                      <div style={{ width: '100%' }}>
                        <DatePicker
                          value={deliveryDate}
                          onChange={(d) => setDeliveryDate(d)}
                          minDate={todayStart}
                          maxDate={effectivePickupLimit ? new Date(effectivePickupLimit.getTime() - 1) : undefined}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {userAddresses.map((address) => (
                  <div
                    key={address.id}
                    className={`address-item ${selectedAddressId === address.id ? 'selected' : ''} ${address.isDefault ? 'default' : ''}`}
                    onClick={() => handleSelectAddress(address)}
                    style={{
                      border: selectedAddressId === address.id ? '2px solid #079d9a' : '1px solid #dee2e6',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '12px',
                      cursor: 'pointer',
                      backgroundColor: selectedAddressId === address.id ? '#f8f9fa' : 'white'
                    }}
                  >
                    <div className="d-flex align-items-start justify-content-between">
                      <div className="d-flex align-items-start" style={{ flex: 1 }}>
                        <div style={{ marginTop: '4px' }}>
                          <input
                            type="radio"
                            name="address"
                            className="custom-radio-delivery"
                            checked={selectedAddressId === address.id}
                            onChange={() => handleSelectAddress(address)}
                          />
                        </div>

                        <div style={{ marginLeft: '12px', flex: 1 }}>
                          <div className="d-flex align-items-center mb-2">
                            <span style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#212529'
                            }}>
                              {address.name}
                            </span>
                            {address.isDefault && (
                              <span className="default-badge" style={{
                                marginLeft: '8px',
                                backgroundColor: '#079d9a',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px'
                              }}>Default</span>
                            )}
                          </div>

                          <div style={{
                            fontSize: '14px',
                            color: '#6c757d',
                            lineHeight: '1.4'
                          }}>
                            <div>{address.address?.street || address.address}</div>
                            <div>Postal Code: {address.postalCode}</div>
                            <div>Consignee: {address.consignee}</div>
                            <div>Phone: {address.phone}</div>
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-center" style={{ gap: '8px', marginLeft: '8px' }}>
                        <i
                          className="icon_pencil-edit"
                          style={{
                            cursor: 'pointer',
                            color: '#079d9a',
                            fontSize: '16px',
                            padding: '4px'
                          }}
                          onClick={(e) => handleEditClick(address, e)}
                          title="Edit address"
                        />
                        <i
                          className="icon_trash"
                          style={{
                            cursor: 'pointer',
                            color: 'grey',
                            fontSize: '16px',
                            padding: '4px'
                          }}
                          onClick={(e) => handleDeleteClick(address.id, e)}
                          title="Delete address"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div
                  className="mt-0 pt-1"
                  style={{
                    textAlign: 'right'
                  }}
                >
                  <span
                    onClick={handleAddNew}
                    style={{
                      fontSize: '14px',
                      color: '#079d9a',
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    Add New Address
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p style={{ color: '#6c757d', marginBottom: '16px', fontSize: '19px' }}>
                  You don&#39;t have any saved addresses yet.
                </p>
                <button
                  type="button"
                  className="btn btn-primary mx-auto"
                  onClick={handleAddNew}
                  style={{ borderRadius: '23px', width: '80%' }}
                >
                  Add Your First Address
                </button>
              </div>
            )
          )}
        </ModalBody>
        <ModalFooter style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingAddress ? (
            <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'flex-end' }}>
              <Button color="light" onClick={handleEditCancel} disabled={isLoading} style={{ border: '1px solid #07C3C3' }}>
                <span style={{ color: '#07C3C3' }}>Cancel</span>
              </Button>
              <Button
                color="primary"
                onClick={handleEditSave}
                disabled={isLoading}
                style={{ borderRadius: '25px' }}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          ) : (
            userAddresses.length > 0 && (
              <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'flex-end' }}>
                <Button color="light" onClick={toggle} style={{ border: '1px solid #07C3C3' }}>
                  <span style={{ color: '#07C3C3' }}>Cancel</span>
                </Button>
                <Button
                  color="primary"
                  onClick={handleConfirm}
                  disabled={!selectedAddressId || isLoading}
                  style={{ borderRadius: '25px' }}
                >
                  {isLoading ? 'Updating...' : 'Update Address'}
                </Button>
              </div>
            )
          )}
        </ModalFooter>
      </Modal>

      {userInfo?.phone && (
        <PurchaseDetailAddAddressModal
          isOpen={isAddNewAddressModalOpen}
          toggle={() => setIsAddNewAddressModalOpen(false)}
          onAddressAdded={handleAddressAdded}
          onAddressUpdated={onAddressUpdated}
          orderRef={orderRef}
          phoneNumber={userInfo?.phone || ''}
          userAddresses={userAddresses}
        />
      )}
    </>
  )
}

export default AmendDeliveryAddressModal
